import { Menu } from '@beeline.com/bp-design-system';
import React, { FC, useCallback, useState } from 'react';
import { useSupportLinks } from 'src/components/pages/settings/userInterfaceSettings/general/clientSupportLinks/useSupportLinks';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { AppTopBarButtonDetails, ExternalLinkDetails } from 'src/integrations/mixpanel/Events/general';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { useFeatureFlags } from '../featureFlag/useFeatureFlags';
import { HeaderAction } from './HeaderAction';
import { HelpButton } from './HelpButton';
import { MenuListSubHeader, SupportLinks } from './SupportLinks';
import { useHeaderIntl } from './translations/useHeaderIntl';
import { useDailyBriefingEnabled } from 'src/components/pages/dailyBriefingSidePanel/useDailyBriefingEnabled';

const Links = {
  Feedback: 'https://portal.productboard.com/yhdhyanqaf9ddhrvubw4dtmc/tabs/153-ideas-under-consideration',
  Support: 'https://beelineco.atlassian.net/servicedesk/customer/portal/19'
};

interface Props {
  onHelpClick: () => void;
  isHelpOpen: boolean;
  onBriefingClick: () => void;
}

export const HelpMenu: FC<React.PropsWithChildren<Props>> = ({ onHelpClick, isHelpOpen, onBriefingClick }) => {
  const { clientSupportLinks: isSupportLinksEnabled } = useDarkly();
  const isDailyBriefingEnabled = useDailyBriefingEnabled();
  const { supportLinks } = useSupportLinks();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  const { track } = useMixpanel();
  const [isOpen, setOpen] = useState(false);
  const intl = useHeaderIntl();
  const { isEnabled } = useFeatureFlags();
  const isHelpFeatureOn = isEnabled('in-app-help');
  const isSupportLinkOn = isEnabled('customizable-support-links');

  const handleMenu = (target: HTMLElement) => {
    track<AppTopBarButtonDetails>(MixpanelEvents.general.APP_TOP_BAR_BUTTON, { origin: 'help' });
    setOpen(true);
    setAnchorEl(target);
  };

  const trackLink = useCallback(
    (label: string, url: string, isCustomClientLink: boolean) => {
      const details: ExternalLinkDetails = { label: label, url: url, customClientLink: isCustomClientLink };
      track<ExternalLinkDetails>(MixpanelEvents.general.EXTERNAL_LINK_CLICK, details);
    },
    [track]
  );

  const handleClose = () => {
    track(MixpanelEvents.helpDocs.HELP_MENU_CLOSE);
    setOpen(false);
    setAnchorEl(undefined);
  };

  const handleClientLinkClick = (label: string, url: string) => {
    handleClose();
    trackLink(label, url, true);
  };

  const showSupportLinks = isSupportLinksEnabled && isSupportLinkOn && supportLinks.length > 0;

  return (
    <>
      <HelpButton isHelpOpen={isHelpOpen} onHelpClick={handleMenu} />
      <Menu
        id="menu-app-bar-help"
        dataTest="menu-app-bar-help-popover"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: paperStyles
          }
        }}
      >
        {showSupportLinks && <MenuListSubHeader>Beeline Professional</MenuListSubHeader>}
        {isHelpFeatureOn && (
          <HeaderAction
            dataTest="in-app-help-docs-drawer-link"
            text={intl.documentation}
            onClick={() => {
              handleClose();
              onHelpClick();
            }}
          />
        )}
        {isDailyBriefingEnabled && (
          <HeaderAction
            dataTest="daily-briefing-drawer-link"
            text={intl.dailyBriefing}
            onClick={() => {
              handleClose();
              onBriefingClick();
            }}
          />
        )}
        <HeaderAction
          dataTest="feekback-link"
          text={intl.feedback}
          rel="noopener noreferrer"
          target="_blank"
          href={Links.Feedback}
          onClick={() => {
            handleClose();
            trackLink('Feedback', Links.Feedback, false);
          }}
          icon="open_in_new"
        />
        <HeaderAction
          dataTest="support-link"
          text={intl.support}
          rel="noopener noreferrer"
          target="_blank"
          href={Links.Support}
          onClick={() => {
            handleClose();
            trackLink('Support', Links.Support, false);
          }}
          icon="open_in_new"
        />
        {showSupportLinks && <SupportLinks links={supportLinks} onClick={handleClientLinkClick} />}
      </Menu>
    </>
  );
};

const paperStyles: React.CSSProperties = {
  padding: `0 0.5rem`
};
